import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AdminLayout } from '../components/layout/AdminLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { MediaPicker } from '../components/forms/MediaPicker';
import { RichTextEditor } from '../components/forms/RichTextEditor';
import {
  useArticleById,
  useCreateArticle,
  useUpdateArticle,
  usePublishArticle,
  useUnpublishArticle,
} from '../hooks/useArticles';
import { useCategories } from '../hooks/useCategories';
import { useTags } from '../hooks/useTags';
import { ArrowLeft, Loader2, Save, X, Eye, EyeOff, CheckCircle, FileText } from 'lucide-react';
import { generateSlug } from '../lib/utils';
import type { Article } from '../types/entities';

type ArticleWithTags = Article & { articleTags?: { tagId: string }[] };

const articleSchema = z.object({
  title: z.string().min(1, 'Titre requis'),
  slug: z.string().min(1, 'Slug requis'),
  summary: z.string().min(1, 'Résumé requis'),
  content: z.string().min(1, 'Contenu requis'),
  featuredImageUrl: z.string().nullable(),
  categoryId: z.string().nullable(),
  tagIds: z.array(z.string()).optional(),
});

type ArticleFormData = z.infer<typeof articleSchema>;

export default function ArticleForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = !!id;

  const { data: article, isLoading: isLoadingArticle } = useArticleById(id || '');
  const { data: categories } = useCategories();
  const { data: tags } = useTags();
  const createArticle = useCreateArticle();
  const updateArticle = useUpdateArticle();
  const publishArticle = usePublishArticle();
  const unpublishArticle = useUnpublishArticle();

  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<ArticleFormData>({
    resolver: zodResolver(articleSchema),
    defaultValues: {
      title: '',
      slug: '',
      summary: '',
      content: '',
      featuredImageUrl: null,
      categoryId: null,
      tagIds: [],
    },
  });

  const featuredImageUrl = watch('featuredImageUrl');
  const content = watch('content');
  const title = watch('title');

  // Load article data when editing
  useEffect(() => {
    if (article && isEditing) {
      const a = article as ArticleWithTags;
      const tagIds = a.articleTags?.map((at) => at.tagId) || [];
      reset({
        title: article.title,
        slug: article.slug,
        summary: article.summary,
        content: article.content,
        featuredImageUrl: article.featuredImageUrl,
        categoryId: article.categoryId || null,
        tagIds,
      });
      setSelectedTagIds(tagIds);
    }
  }, [article, isEditing, reset]);

  const onSubmit = async (data: ArticleFormData) => {
    const submissionData = {
      ...data,
      tagIds: selectedTagIds,
    };

    if (isEditing && id) {
      await updateArticle.mutateAsync({ id, data: submissionData });
    } else {
      await createArticle.mutateAsync(submissionData);
    }
    navigate('/articles');
  };

  const handleAddTag = (tagId: string) => {
    if (!selectedTagIds.includes(tagId)) {
      const newTagIds = [...selectedTagIds, tagId];
      setSelectedTagIds(newTagIds);
      setValue('tagIds', newTagIds, { shouldDirty: true });
    }
  };

  const handleRemoveTag = (tagId: string) => {
    const newTagIds = selectedTagIds.filter(id => id !== tagId);
    setSelectedTagIds(newTagIds);
    setValue('tagIds', newTagIds, { shouldDirty: true });
  };

  // Auto-generate slug from title (only when creating)
  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTitle = e.target.value;
    setValue('title', newTitle, { shouldDirty: true });
    if (!isEditing) {
      setValue('slug', generateSlug(newTitle), { shouldDirty: true });
    }
  };

  const isSubmitting = createArticle.isPending || updateArticle.isPending;

  if (isEditing && isLoadingArticle) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[50vh]">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <form onSubmit={handleSubmit(onSubmit)} className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-gray-50 border-b -mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8 py-4 mb-6">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => navigate('/articles')}
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                    {isEditing ? 'Modifier l\'article' : 'Nouvel article'}
                  </h1>
                  {isEditing && article && (
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                      article.published
                        ? 'bg-green-100 text-green-700'
                        : 'bg-amber-100 text-amber-700'
                    }`}>
                      {article.published ? 'Publié' : 'Brouillon'}
                    </span>
                  )}
                </div>
                {title && (
                  <p className="text-sm text-gray-500 truncate max-w-xs sm:max-w-md">
                    {title}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/articles')}
                className="hidden sm:flex"
              >
                Annuler
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    <span className="hidden sm:inline">
                      {isEditing ? 'Enregistrer' : 'Créer (brouillon)'}
                    </span>
                    <span className="sm:hidden">
                      {isEditing ? 'Enregistrer' : 'Créer'}
                    </span>
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>

        <div className="space-y-6 pb-24">
          {/* Main content - 2 columns on desktop */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left column - Main content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Title */}
              <div className="bg-white rounded-lg border p-4 sm:p-6 space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Titre *</Label>
                  <Input
                    id="title"
                    {...register('title')}
                    onChange={handleTitleChange}
                    placeholder="Le titre de votre article"
                    className={`text-lg ${errors.title ? 'border-red-500' : ''}`}
                  />
                  {errors.title && (
                    <p className="text-sm text-red-600">{errors.title.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="slug">Slug (URL)</Label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">/blog/</span>
                    <Input
                      id="slug"
                      {...register('slug')}
                      placeholder="mon-article"
                      className={errors.slug ? 'border-red-500' : ''}
                    />
                  </div>
                  {errors.slug && (
                    <p className="text-sm text-red-600">{errors.slug.message}</p>
                  )}
                </div>
              </div>

              {/* Summary */}
              <div className="bg-white rounded-lg border p-4 sm:p-6">
                <div className="space-y-2">
                  <Label htmlFor="summary">Résumé *</Label>
                  <p className="text-xs text-gray-500">
                    Court texte affiché dans les listes et aperçus
                  </p>
                  <Textarea
                    id="summary"
                    {...register('summary')}
                    rows={3}
                    placeholder="Un bref résumé de votre article..."
                    className={`resize-none ${errors.summary ? 'border-red-500' : ''}`}
                  />
                  {errors.summary && (
                    <p className="text-sm text-red-600">{errors.summary.message}</p>
                  )}
                </div>
              </div>

              {/* Content */}
              <div className="bg-white rounded-lg border p-4 sm:p-6">
                <div className="space-y-2">
                  <Label>Contenu *</Label>
                  <RichTextEditor
                    value={content}
                    onChange={(value) => setValue('content', value, { shouldDirty: true })}
                    placeholder="Commencez à écrire votre article..."
                    error={errors.content?.message}
                  />
                </div>
              </div>
            </div>

            {/* Right column - Sidebar */}
            <div className="space-y-6">
              {/* Publication Status - New article info */}
              {!isEditing && (
                <div className="bg-amber-50 rounded-lg border border-amber-200 p-4 sm:p-6">
                  <div className="flex items-start gap-3">
                    <FileText className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-amber-800">
                        Création en brouillon
                      </p>
                      <p className="text-sm text-amber-700 mt-1">
                        L'article sera créé comme brouillon. Vous pourrez le publier après l'avoir enregistré.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Publication Status - Existing article */}
              {isEditing && article && (
                <div className="bg-white rounded-lg border p-4 sm:p-6">
                  <Label className="mb-3 block">Statut de publication</Label>
                  <div className="space-y-3">
                    {/* Status Badge */}
                    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${
                      article.published
                        ? 'bg-green-100 text-green-800'
                        : 'bg-amber-100 text-amber-800'
                    }`}>
                      {article.published ? (
                        <>
                          <CheckCircle className="w-4 h-4" />
                          Publié
                        </>
                      ) : (
                        <>
                          <FileText className="w-4 h-4" />
                          Brouillon
                        </>
                      )}
                    </div>

                    {/* Published Date */}
                    {article.published && article.publishedAt && (
                      <p className="text-sm text-gray-500">
                        Publié le {new Date(article.publishedAt).toLocaleDateString('fr-FR', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    )}

                    {/* Publish/Unpublish Button */}
                    {article.published ? (
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full"
                        onClick={() => unpublishArticle.mutate(id!)}
                        disabled={unpublishArticle.isPending}
                      >
                        {unpublishArticle.isPending ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <EyeOff className="w-4 h-4 mr-2" />
                        )}
                        Dépublier
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        className="w-full bg-green-600 hover:bg-green-700"
                        onClick={() => publishArticle.mutate(id!)}
                        disabled={publishArticle.isPending}
                      >
                        {publishArticle.isPending ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Eye className="w-4 h-4 mr-2" />
                        )}
                        Publier
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* Featured Image */}
              <div className="bg-white rounded-lg border p-4 sm:p-6">
                <Label className="mb-3 block">Image à la une</Label>
                <MediaPicker
                  value={featuredImageUrl}
                  onChange={(url) => setValue('featuredImageUrl', url, { shouldDirty: true })}
                />
              </div>

              {/* Category */}
              <div className="bg-white rounded-lg border p-4 sm:p-6">
                <div className="space-y-2">
                  <Label htmlFor="categoryId">Catégorie</Label>
                  <select
                    id="categoryId"
                    {...register('categoryId')}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    <option value="">Aucune catégorie</option>
                    {categories?.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Tags */}
              <div className="bg-white rounded-lg border p-4 sm:p-6">
                <div className="space-y-3">
                  <Label>Tags</Label>

                  {/* Selected tags */}
                  {selectedTagIds.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {selectedTagIds.map((tagId) => {
                        const tag = tags?.find(t => t.id === tagId);
                        if (!tag) return null;
                        const tagColor = tag.color || '#6b7280';
                        return (
                          <span
                            key={tagId}
                            className="inline-flex items-center gap-1 px-2 py-1 text-sm rounded"
                            style={{
                              backgroundColor: `${tagColor}20`,
                              color: tagColor,
                              border: `1px solid ${tagColor}`,
                            }}
                          >
                            {tag.name}
                            <button
                              type="button"
                              onClick={() => handleRemoveTag(tagId)}
                              className="hover:opacity-70"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  )}

                  {/* Add tag */}
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value=""
                    onChange={(e) => {
                      if (e.target.value) {
                        handleAddTag(e.target.value);
                      }
                    }}
                  >
                    <option value="">Ajouter un tag...</option>
                    {tags
                      ?.filter(tag => !selectedTagIds.includes(tag.id))
                      .map((tag) => (
                        <option key={tag.id} value={tag.id}>
                          {tag.name}
                        </option>
                      ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </AdminLayout>
  );
}
